import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  registerWithEmail,
  signInWithEmail,
  signInWithGoogle,
  signOut as firebaseSignOut,
  AuthUser,
  isAuthenticated as checkAuth,
} from '../services/authService';
import { isFirebaseEnabled } from '../services/firebaseConfig';
import { getUserHouseholdId } from '../services/householdService';
import { setActiveHouseholdId, clearActiveHouseholdId } from '../services/householdContext';
import { AUTH_NETWORK_ERROR_CODE } from '../utils/authErrors';

/**
 * Normalized auth error codes the identifier-first UI branches on, so the
 * screen can pick the right treatment (inline field error vs warning banner
 * vs offline banner) without re-parsing raw Firebase codes.
 */
export type AuthErrorCode =
  | 'bad-credentials'
  | 'too-many-requests'
  | 'network'
  | 'invalid-email'
  | 'email-in-use'
  | 'weak-password'
  | 'google-cancelled'
  | 'generic';

export interface AuthErrorPayload {
  message: string;
  code: AuthErrorCode;
}

function mapFirebaseAuthError(error: any): AuthErrorPayload {
  switch (error?.code) {
    case 'auth/invalid-credential':
    case 'auth/user-not-found':
    case 'auth/wrong-password':
      return { message: 'Incorrect email or password. Please try again.', code: 'bad-credentials' };
    case 'auth/email-already-in-use':
      return { message: 'An account with this email already exists. Sign in instead.', code: 'email-in-use' };
    case 'auth/weak-password':
      return { message: 'Password must be at least 6 characters.', code: 'weak-password' };
    case 'auth/too-many-requests':
      return { message: 'Too many failed attempts. Please wait a moment and try again.', code: 'too-many-requests' };
    case AUTH_NETWORK_ERROR_CODE:
      return { message: 'Network error. Please check your connection.', code: 'network' };
    case 'auth/invalid-email':
      return { message: 'Invalid email address.', code: 'invalid-email' };
    default:
      return { message: error?.message || 'Authentication failed. Please try again.', code: 'generic' };
  }
}

/**
 * Map Google OAuth failures. Popup-closed/cancelled is a benign "user backed
 * out" (info banner), distinct from a real network failure.
 */
function mapGoogleAuthError(error: any): AuthErrorPayload {
  const code: string = error?.code || '';
  const message: string = error?.message || '';
  if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request' || /cancel/i.test(message)) {
    return { message: message || 'Google sign-in was interrupted.', code: 'google-cancelled' };
  }
  if (code === AUTH_NETWORK_ERROR_CODE || /network|offline/i.test(message)) {
    return { message: message || 'Network error. Please check your connection.', code: 'network' };
  }
  return { message: message || 'Google sign-in failed', code: 'generic' };
}

interface AuthState {
  user: AuthUser | null;
  token: string | null;
  loading: boolean;
  error: string | null;
  // Normalized code for the last error, so the sign-in screen can branch on
  // it (field error vs banner). `error` stays a human string for existing
  // consumers (the onboarding finish and the settings screen).
  errorCode: AuthErrorCode | null;
  isAuthenticated: boolean;
  isGuest: boolean; // Track if user is in guest mode
  // Household pairing: present when the user is in a 2-person household.
  // Sync middleware uses this to route shared data under
  // households/{householdId}/... instead of users/{uid}/...
  householdId: string | null;
  // Bumped by every deliberate change of household (pairing, leaving, a
  // different account). A profile read that started before the latest bump
  // is stale and must not overwrite it (#776). Optional: persisted states
  // from before it existed have none.
  householdRevision?: number;
}

const initialState: AuthState = {
  user: null,
  token: null,
  loading: false,
  error: null,
  errorCode: null,
  isAuthenticated: false,
  isGuest: true, // Start in guest mode
  householdId: null,
  householdRevision: 0,
};

const bumpHouseholdRevision = (state: AuthState) => {
  state.householdRevision = (state.householdRevision ?? 0) + 1;
};

/** Which household decision a profile read started from. */
interface HouseholdReadMeta {
  revision: number;
}

/**
 * True when the household changed on purpose after this read began.
 *
 * Sign-in starts a profile read, and a brand-new account's says "no household".
 * If the cook opens the Share screen and creates one before that read returns,
 * applying its answer resets the household to none: every synced slice falls
 * back to `users/{uid}/…` and the screen offers to create a second household
 * (#776). A read never outranks a decision made after it started.
 */
const isStaleHouseholdRead = (
  state: AuthState,
  meta: Partial<HouseholdReadMeta>
): boolean => meta.revision !== undefined && meta.revision !== (state.householdRevision ?? 0);

/**
 * Register with email and password
 */
export const registerUser = createAsyncThunk(
  'auth/registerUser',
  async ({ email, password }: { email: string; password: string }, { rejectWithValue }) => {
    try {
      if (!isFirebaseEnabled()) {
        throw new Error('Firebase not configured. Running in offline mode.');
      }
      const user = await registerWithEmail(email, password);
      return { user };
    } catch (error: any) {
      return rejectWithValue(mapFirebaseAuthError(error));
    }
  }
);

/**
 * Login with email and password
 */
export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async ({ email, password }: { email: string; password: string }, { rejectWithValue }) => {
    try {
      if (!isFirebaseEnabled()) {
        throw new Error('Firebase not configured. Running in offline mode.');
      }
      const user = await signInWithEmail(email, password);
      return { user };
    } catch (error: any) {
      return rejectWithValue(mapFirebaseAuthError(error));
    }
  }
);

/**
 * Login with Google
 */
export const loginWithGoogle = createAsyncThunk(
  'auth/loginWithGoogle',
  async (_, { rejectWithValue }) => {
    try {
      if (!isFirebaseEnabled()) {
        throw new Error('Firebase not configured. Running in offline mode.');
      }
      // `isNewUser` rides along so analyticsMiddleware can tell a first-time
      // Google signup from a returning sign-in — the same button does both.
      const { user, isNewUser } = await signInWithGoogle();
      return { user, isNewUser };
    } catch (error: any) {
      return rejectWithValue(mapGoogleAuthError(error));
    }
  }
);

/**
 * Load the signed-in user's householdId from their profile doc. Called
 * after auth state change resolves so sync middleware can route shared data
 * under households/{hid}/... when the user is paired.
 */
export const loadHouseholdId = createAsyncThunk<
  string | null,
  string,
  {
    state: { auth: AuthState };
    rejectValue: string;
    fulfilledMeta: HouseholdReadMeta;
    rejectedMeta: HouseholdReadMeta;
  }
>(
  'auth/loadHouseholdId',
  async (uid, { getState, fulfillWithValue, rejectWithValue }) => {
    const meta: HouseholdReadMeta = { revision: getState().auth?.householdRevision ?? 0 };
    try {
      if (!isFirebaseEnabled()) return fulfillWithValue(null, meta);
      return fulfillWithValue(await getUserHouseholdId(uid), meta);
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to load household', meta);
    }
  }
);

/**
 * Logout
 */
export const logout = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      if (isFirebaseEnabled()) {
        await firebaseSignOut();
      }
      return null;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Logout failed');
    }
  }
);

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // Set user from Firebase auth state listener
    setUser: (state, action: PayloadAction<AuthUser | null>) => {
      // A household belongs to the account that joined it. `auth` is in the
      // redux-persist root whitelist, so householdId survives sign-out in
      // localStorage — without this, signing in as a second account in the
      // same browser inherited the first account's scope. That is not just a
      // confusing /join screen: householdId decides which Firestore path
      // every synced slice reads and writes, so a stale one points a new
      // account at another household's data.
      //
      // Scoped to an actual identity change. Firebase re-emits the same user
      // on token refresh and on every app boot; clearing unconditionally
      // would drop the household on each one and flicker the week back to
      // personal scope until the profile reloads.
      if (state.user?.uid !== action.payload?.uid) {
        state.householdId = null;
        clearActiveHouseholdId();
        bumpHouseholdRevision(state);
      }
      state.user = action.payload;
      state.isAuthenticated = action.payload !== null;
      state.isGuest = action.payload === null;
    },
    // Continue as guest
    continueAsGuest: (state) => {
      state.isGuest = true;
      state.isAuthenticated = false;
      state.user = null;
      state.householdId = null;
      bumpHouseholdRevision(state);
    },
    // Set the user's householdId after profile load or pairing flow.
    // Also mirrors into the module-level household context so services that
    // can't easily reach Redux (e.g. unifiedRecipeService) pick up the
    // active scope without callers threading it through.
    setHouseholdId: (state, action: PayloadAction<string | null>) => {
      state.householdId = action.payload;
      bumpHouseholdRevision(state);
      if (action.payload) {
        setActiveHouseholdId(action.payload);
      } else {
        clearActiveHouseholdId();
      }
    },
    // Clear error
    clearError: (state) => {
      state.error = null;
      state.errorCode = null;
    },
    // Reset loading state (used after redirect-based auth)
    resetLoading: (state) => {
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Register
      .addCase(registerUser.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.errorCode = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.error = null;
        state.errorCode = null;
        state.isAuthenticated = true;
        state.isGuest = false;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false;
        const payload = action.payload as AuthErrorPayload;
        state.error = payload?.message ?? 'Authentication failed. Please try again.';
        state.errorCode = payload?.code ?? 'generic';
      })
      // Login
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.errorCode = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.error = null;
        state.errorCode = null;
        state.isAuthenticated = true;
        state.isGuest = false;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        const payload = action.payload as AuthErrorPayload;
        state.error = payload?.message ?? 'Authentication failed. Please try again.';
        state.errorCode = payload?.code ?? 'generic';
      })
      // Google Login
      .addCase(loginWithGoogle.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.errorCode = null;
      })
      .addCase(loginWithGoogle.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.error = null;
        state.errorCode = null;
        state.isAuthenticated = true;
        state.isGuest = false;
      })
      .addCase(loginWithGoogle.rejected, (state, action) => {
        state.loading = false;
        const payload = action.payload as AuthErrorPayload;
        state.error = payload?.message ?? 'Google sign-in failed';
        state.errorCode = payload?.code ?? 'generic';
      })
      // Household load — sets state and mirrors into module context so
      // services that can't easily reach Redux (unifiedRecipeService etc.)
      // pick up the active scope without callers threading it through.
      .addCase(loadHouseholdId.fulfilled, (state, action) => {
        if (isStaleHouseholdRead(state, action.meta)) return;
        const householdId = action.payload ?? null;
        state.householdId = householdId;
        if (householdId) {
          setActiveHouseholdId(householdId);
        } else {
          clearActiveHouseholdId();
        }
      })
      .addCase(loadHouseholdId.rejected, (state, action) => {
        if (isStaleHouseholdRead(state, action.meta)) return;
        state.householdId = null;
        clearActiveHouseholdId();
      })
      // Logout
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.loading = false;
        state.error = null;
        state.isAuthenticated = false;
        state.isGuest = true;
        state.householdId = null;
        clearActiveHouseholdId();
        bumpHouseholdRevision(state);
      });
  },
});

export const { setUser, continueAsGuest, clearError, resetLoading, setHouseholdId } = authSlice.actions;
export default authSlice.reducer;

/**
 * A real account, not a guest. Both flags matter: `isGuest` starts true and
 * only `continueAsGuest` clears it, while `isAuthenticated` is what a real
 * sign-in sets. Typed structurally so this file need not import the store.
 */
export const selectHasAccount = (state: {
  auth?: { isAuthenticated?: boolean; isGuest?: boolean };
}): boolean => Boolean(state.auth?.isAuthenticated) && !state.auth?.isGuest;
